"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.calculateContingencies = exports.calculateContingencyForFuture = exports.getIVMoves = exports.calculateMarginsForAccount = exports.calculateMarginsForPosition = exports.calculateOptionRiskValues = exports.calculatePositionSmMargin = exports.calculateFutureRiskValues = void 0;
const types_1 = require("../types");
const calculationUtils_1 = require("./calculationUtils");
const blackScholes_1 = require("./blackScholes");
const marginUtils_1 = require("./marginUtils");
const marginParameters_1 = require("./marginParameters");
const OPTIONS_CONTINGENCY_MULTIPLER = 0.01;
const calculateFutureRiskValues = (amount, ivMoves, underlyingPrice) => {
    const ivMovesForFuture = [];
    for (const move of ivMoves) {
        const risk = (move / (1 + move)) * (amount / underlyingPrice);
        ivMovesForFuture.push([risk, risk, risk]);
    }
    return ivMovesForFuture;
};
exports.calculateFutureRiskValues = calculateFutureRiskValues;
const calculateSmMarginsLinearPerpetual = (position) => {
    const marginParams = marginParameters_1.paramsMap[(0, calculationUtils_1.getBaseCurrencyFromInstrumentString)(position.instrument)];
    let amount = position.amount;
    const size = Math.abs(amount);
    const mm = (marginParams.maintenanceMarginStart +
        marginParams.marginIncrement * size) *
        size;
    const im = (marginParams.initialMarginStart + marginParams.marginIncrement * size) *
        size;
    return { im, mm, contingency: 0 };
};
const calculatePositionSmMargin = (position, index, ticker) => {
    let im = 0;
    let mm = 0;
    if ((0, calculationUtils_1.isLinearPerpetual)(position.instrument)) {
        const { im, mm } = calculateSmMarginsLinearPerpetual(position);
        return {
            contingency: 0,
            im: im * ticker.markPrice,
            mm: mm * ticker.markPrice,
        };
    }
    const marginParams = marginParameters_1.paramsMap[(0, calculationUtils_1.getBaseCurrencyFromInstrumentString)(position.instrument)];
    let amount = position.amount;
    const positionType = (0, calculationUtils_1.getInstrumentType)(position.instrument);
    let size = positionType === types_1.PositionType.Future ? amount / index : amount;
    size = Math.abs(size);
    if (positionType === types_1.PositionType.Future) {
        mm =
            (marginParams.maintenanceMarginStart +
                marginParams.marginIncrement * size) *
                size;
        im =
            (marginParams.initialMarginStart + marginParams.marginIncrement * size) *
                size;
    }
    else {
        if (amount > 0) {
            mm = 0;
            im = 0;
        }
        else {
            const { optionType, strike } = (0, calculationUtils_1.getOptionInstrumentProperties)(position.instrument);
            if (optionType === types_1.OptionType.CALL) {
                mm = (0.075 + ticker.markPrice) * size;
                im =
                    (ticker.markPrice +
                        Math.max(0.1, 0.15 -
                            Math.max(0, strike - ticker.underlyingPrice) /
                                ticker.underlyingPrice)) *
                        size;
            }
            else {
                mm =
                    (Math.max(0.075, 0.075 * ticker.markPrice) + ticker.markPrice) * size;
                im = Math.max((ticker.markPrice +
                    Math.max(0.1, 0.15 -
                        Math.max(0, ticker.underlyingPrice - strike) /
                            ticker.underlyingPrice)) *
                    size, mm);
            }
        }
    }
    return { im, mm, contingency: 0 };
};
exports.calculatePositionSmMargin = calculatePositionSmMargin;
const calculateOptionRiskValues = (option, ivMoves, ticker) => {
    const currency = option.instrument.split("-")[0];
    const marginParams = marginParameters_1.paramsMap[currency];
    const ivRangeUp = marginParams.volatilityRangeUp;
    const ivRangeDown = marginParams.volatilityRangeDown;
    const shortTermVegaPower = 0.3;
    const longTermVegaPower = 0.13;
    const ivMovesForOption = [];
    for (const move of ivMoves) {
        const simulatedUnderlyingPrice = ticker.underlyingPrice * (1 + move);
        const expiryTimestamp = (0, calculationUtils_1.getExpiryTimestampFromInstrumentString)(option.instrument);
        const yearsToExpiry = (0, calculationUtils_1.getTimeInYearsBetweenDates)(new Date().getTime(), expiryTimestamp);
        const daysToExpiry = 365 * yearsToExpiry;
        const vegaPower = daysToExpiry < 30 ? shortTermVegaPower : longTermVegaPower;
        const simulatedIVDown = calculateSimulatedVolatility(ticker.volatility / 100, daysToExpiry, ivRangeDown, vegaPower);
        const simulatedIVSame = calculateSimulatedVolatility(ticker.volatility / 100, daysToExpiry, 0, vegaPower);
        const simulatedIVUp = calculateSimulatedVolatility(ticker.volatility / 100, daysToExpiry, ivRangeUp, vegaPower);
        const optionPmMarginDown = calculateOptionPmMargin(option, ticker, simulatedUnderlyingPrice, simulatedIVDown, yearsToExpiry);
        const optionPmMarginSame = calculateOptionPmMargin(option, ticker, simulatedUnderlyingPrice, simulatedIVSame, yearsToExpiry);
        const optionPmMarginUp = calculateOptionPmMargin(option, ticker, simulatedUnderlyingPrice, simulatedIVUp, yearsToExpiry);
        ivMovesForOption.push([
            optionPmMarginDown,
            optionPmMarginSame,
            optionPmMarginUp,
        ]);
    }
    return ivMovesForOption;
};
exports.calculateOptionRiskValues = calculateOptionRiskValues;
const calculateMarginsForPosition = (position, index, marginType, ticker) => {
    return (0, exports.calculatePositionSmMargin)(position, index, ticker);
};
exports.calculateMarginsForPosition = calculateMarginsForPosition;
const calculateMarginsForAccount = (rows, equity, marginType, tickers, tablesCalculations) => {
    const margins = marginType === types_1.MarginType.PM
        ? calculateAccountMarginsPM(rows, equity, tickers, tablesCalculations)
        : calculateAccountMarginsSM(rows, equity, tickers);
    if (equity === 0 && margins.mm === 0) {
        return Object.assign({}, margins);
    }
    return margins;
};
exports.calculateMarginsForAccount = calculateMarginsForAccount;
const getIVMoves = (currency) => {
    return currency === types_1.Currency.ALL || currency === types_1.Currency.USDC
        ? marginParameters_1.paramsMap[types_1.Currency.BTC].ivMoves
        : marginParameters_1.paramsMap[currency].ivMoves;
};
exports.getIVMoves = getIVMoves;
const calculateContingencyForFuture = (future, underlyingPrice) => {
    const curency = future.instrument.split("-")[0];
    const marginParams = marginParameters_1.paramsMap[curency];
    const amount = future.amount;
    return (amount / underlyingPrice) * marginParams.futureContingency;
};
exports.calculateContingencyForFuture = calculateContingencyForFuture;
const calculateContingencies = (positions, tickers) => {
    let res = {};
    const futures = positions.filter((p) => (0, calculationUtils_1.getInstrumentType)(p.instrument) === types_1.PositionType.Future);
    const options = positions.filter((p) => (0, calculationUtils_1.getInstrumentType)(p.instrument) === types_1.PositionType.Option);
    for (const future of futures) {
        res[future.uuid] = (0, exports.calculateContingencyForFuture)(future, tickers[future.instrument].markPrice);
    }
    const optionsContingencies = calculateContingenciesForOptions(options, tickers);
    return Object.assign(Object.assign({}, res), optionsContingencies);
};
exports.calculateContingencies = calculateContingencies;
/**
 * Private
 */
const calculateAccountMarginsPM = (rows, equity, ticker, tablesCalculations) => {
    var _a, _b, _c, _d;
    let mm = 0;
    let mmProjected = 0;
    let pnl = 0;
    let pnlUsd = 0;
    let deltaTotal = 0;
    let deltaTotalProjected = 0;
    let netDeltaTotal = 0;
    let netDeltaTotalProjected = 0;
    const minimumExpiryTimestamp = getMinimumExpiryTimestamp(rows);
    let contingencySum = 0;
    let projectedContingencySum = 0;
    let marginBalance = equity;
    for (const row of rows) {
        const rowCalculations = tablesCalculations.positionsCalculations[row.identifier.uuid];
        const mmPosition = rowCalculations.pmMarginsForMoves[tablesCalculations.worstColumn.moveIndex][tablesCalculations.worstColumn.volChangeIndex];
        contingencySum += rowCalculations.contingency;
        mm += mmPosition;
        deltaTotal += (_a = row.greeks) === null || _a === void 0 ? void 0 : _a.delta;
        netDeltaTotal += (_b = row.greeks) === null || _b === void 0 ? void 0 : _b.netDelta;
        if (row.expiryTimestamp > minimumExpiryTimestamp) {
            mmProjected += mmPosition;
            projectedContingencySum += rowCalculations.contingency;
            deltaTotalProjected += (_c = row.greeks) === null || _c === void 0 ? void 0 : _c.delta;
            netDeltaTotalProjected += (_d = row.greeks) === null || _d === void 0 ? void 0 : _d.netDelta;
        }
        pnl += row.pnl;
        pnlUsd += row.pnlUsd;
    }
    mm = Math.abs(mm) + contingencySum;
    mmProjected = Math.abs(mmProjected) + projectedContingencySum;
    // All rows are in one currency.
    const initialMarginFactor = rows.length > 0
        ? marginParameters_1.paramsMap[(0, calculationUtils_1.getBaseCurrencyFromInstrumentString)(rows[0].instrument)]
            .initialMarginFactor
        : 1;
    const im = mm * initialMarginFactor;
    const imProjected = mmProjected * initialMarginFactor;
    const availableBalance = Math.max(0, marginBalance - Math.abs(im));
    let mmPercent = mm / equity;
    let imPercent = im / equity;
    // Special case https://git.deribit.internal/deribit/frontend/pb-v3/-/issues/76
    if (equity === 0) {
        imPercent = im === 0 ? 0 : im;
        mmPercent = mm === 0 ? 0 : mm;
    }
    return {
        mm,
        mmProjected,
        im,
        imProjected,
        availableBalance,
        marginBalance,
        mmPercent,
        imPercent,
        pnl,
        pnlUsd,
        deltaTotal,
        deltaTotalProjected,
        netDeltaTotal,
        netDeltaTotalProjected,
        imUsd: 0,
        imProjectedUsd: 0,
        mmUsd: 0,
        mmProjectedUsd: 0,
    };
};
const getMinimumExpiryTimestamp = (rows) => {
    const expiryTimestamps = rows
        .filter((r) => r.expiryTimestamp !== 20000000000000)
        .map((r) => r.expiryTimestamp);
    return expiryTimestamps.length ? Math.min(...expiryTimestamps) : 0;
};
// TODO: Redo with table calculations instead of TableRows, delta total usd
const calculateAccountMarginsSM = (rows, equity, ticker) => {
    var _a, _b, _c, _d;
    let mm = 0;
    let mmUsd = 0;
    let mmProjected = 0;
    let mmProjectedUsd = 0;
    let im = 0;
    let imUsd = 0;
    let imProjected = 0;
    let imProjectedUsd = 0;
    let pnl = 0;
    let pnlUsd = 0;
    let marginBalance = equity;
    let deltaTotal = 0;
    let deltaTotalProjected = 0;
    let netDeltaTotal = 0;
    let netDeltaTotalProjected = 0;
    const minimumExpiryTimestamp = getMinimumExpiryTimestamp(rows);
    for (const row of rows) {
        mm += row.margins.mm;
        mmUsd += row.margins.mmUsd;
        im += row.margins.im;
        imUsd += row.margins.imUsd;
        deltaTotal += (_a = row.greeks) === null || _a === void 0 ? void 0 : _a.delta;
        netDeltaTotal += (_b = row.greeks) === null || _b === void 0 ? void 0 : _b.netDelta;
        if (row.expiryTimestamp > minimumExpiryTimestamp) {
            mmProjected += row.margins.mm;
            mmProjectedUsd += row.margins.mmUsd;
            imProjected += row.margins.im;
            imProjectedUsd += row.margins.imUsd;
            deltaTotalProjected += (_c = row.greeks) === null || _c === void 0 ? void 0 : _c.delta;
            netDeltaTotalProjected += (_d = row.greeks) === null || _d === void 0 ? void 0 : _d.netDelta;
        }
        pnl += row.pnl;
        pnlUsd += row.pnlUsd;
        if (row.positionType === types_1.PositionType.Option) {
            let amount = row.updatedAmount ? row.updatedAmount : row.amount;
            marginBalance -= ticker[row.instrument].markPrice * amount;
        }
    }
    const availableBalance = Math.max(0, marginBalance - Math.abs(im));
    let mmPercent = mm === 0 ? 0 : Math.abs(mm) / marginBalance;
    let imPercent = im === 0 ? 0 : Math.abs(im) / marginBalance;
    mmPercent = mmPercent < 0 ? Number.POSITIVE_INFINITY : mmPercent;
    imPercent = imPercent < 0 ? Number.POSITIVE_INFINITY : imPercent;
    // Special case https://git.deribit.internal/deribit/frontend/pb-v3/-/issues/76
    if (equity === 0) {
        imPercent = im === 0 ? 0 : im;
        mmPercent = mm === 0 ? 0 : mm;
    }
    return {
        mm: Math.abs(mm),
        mmUsd,
        mmProjected: Math.abs(mmProjected),
        mmProjectedUsd,
        im: Math.abs(im),
        imUsd,
        imProjected: Math.abs(imProjected),
        imProjectedUsd,
        availableBalance,
        marginBalance,
        mmPercent,
        imPercent,
        pnl,
        pnlUsd,
        deltaTotal,
        deltaTotalProjected,
        netDeltaTotal,
        netDeltaTotalProjected,
    };
};
const calculateSimulatedVolatility = (markIV, daysToExpiry, volChange, exponent) => {
    return Math.max(1 + volChange * Math.pow((30 / daysToExpiry), exponent), 0) * markIV;
};
const calculateOptionPmMargin = (option, ticker, simulatedUnderlyingPrice, simulatedVolatility, t) => {
    const parts = option.instrument.split("-");
    const optionType = parts[3] === "C" ? types_1.OptionType.CALL : types_1.OptionType.PUT;
    const strike = parseInt(parts[2]);
    // TODO we use forward in the port table. For now the platform does not use it.
    const bs = (0, blackScholes_1.blackScholes)(optionType, simulatedUnderlyingPrice, strike, t, 0, simulatedVolatility);
    return (bs / simulatedUnderlyingPrice - ticker.markPrice) * option.amount;
};
const calculateContingencyForOption = (contingencyRow, option, strikeOptions) => {
    if (contingencyRow.netPosition >= 0) {
        return 0;
    }
    const amount = option.amount;
    // total short for the row without rolling. Used to find the proportion of the current position vs total shorts
    const allShortsForStrike = strikeOptions.reduce((memo, current) => {
        const currentAmount = current.amount;
        const shortCurrent = currentAmount < 0 ? currentAmount : 0;
        return shortCurrent + memo;
    }, 0);
    // if we have -10 Put and -20 Call and this is the put it will be 1/3 proportion
    const positionProportionToContingency = amount / allShortsForStrike;
    // From the total short position we have 1/3.
    return (positionProportionToContingency *
        contingencyRow.netPosition *
        OPTIONS_CONTINGENCY_MULTIPLER);
};
const calculateContingenciesForOptions = (options, tickers) => {
    let res = {};
    // TODO: tickers
    if (Object.keys(tickers).length === 0) {
        return res;
    }
    const adjastedStrikesPositions = (0, marginUtils_1.getAdjustStrikesPositions)(options, tickers);
    const groupedOptionsByExpiry = (0, marginUtils_1.groupOptionsByExpiry)(adjastedStrikesPositions);
    for (const expiry of Object.keys(groupedOptionsByExpiry)) {
        const expiryOptions = groupedOptionsByExpiry[expiry];
        const contingencyTable = (0, marginUtils_1.getContingencyTable)(expiryOptions);
        const contingencyTableSortedArray = Object.keys(contingencyTable)
            .reduce((memo, current) => {
            return [...memo, contingencyTable[current]];
        }, [])
            .sort((a, b) => a.strike - b.strike);
        const underlyingPrice = tickers[expiryOptions[0].instrument].underlyingPrice;
        let rolledUp = 0;
        for (let i = 0; i < contingencyTableSortedArray.length; i++) {
            const strikeRow = contingencyTableSortedArray[i];
            if (strikeRow.strike > underlyingPrice) {
                if (strikeRow.netPosition > 0) {
                    rolledUp += strikeRow.netPosition;
                }
                else {
                    strikeRow.netPosition += rolledUp;
                    rolledUp = Math.max(0, strikeRow.netPosition);
                }
            }
        }
        let rolledDown = 0;
        for (let i = contingencyTableSortedArray.length - 1; i >= 0; i--) {
            const strikeRow = contingencyTableSortedArray[i];
            if (strikeRow.strike < underlyingPrice) {
                if (strikeRow.netPosition > 0) {
                    rolledDown += strikeRow.netPosition;
                }
                else {
                    strikeRow.netPosition += rolledDown;
                    rolledDown = Math.max(0, strikeRow.netPosition);
                }
            }
        }
        for (const option of expiryOptions) {
            const amount = option.amount;
            const strike = (0, calculationUtils_1.getOptionInstrumentStrike)(option.instrument);
            const allOptionsForSameStrike = expiryOptions.filter((o) => (0, calculationUtils_1.getOptionInstrumentStrike)(o.instrument) === strike);
            if (amount > 0) {
                res[option.uuid] = 0;
            }
            else {
                const contingencyStrikeRow = contingencyTableSortedArray.find((r) => r.strike === strike);
                const contingencyForOption = calculateContingencyForOption(contingencyStrikeRow, option, allOptionsForSameStrike);
                res[option.uuid] = Math.abs(contingencyForOption);
            }
        }
    }
    return res;
};
